import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertTriangle, Download, Info, Loader2 } from "lucide-react";
import { ExportProgress } from "@/services/call.service";
import {
  CALL_EXPORT_FIELDS,
  CallExportFormat,
  DEFAULT_CALL_EXPORT_FIELD_IDS,
  exportRowLimit,
  hasHeavyExportField,
} from "@/utils/call-export";

const FORMATS: Array<{ value: CallExportFormat; label: string }> = [
  { value: "xlsx", label: "Excel (.xlsx)" },
  { value: "csv", label: "CSV (.csv)" },
];

// A machine that writes 1,1 for one-point-one runs a spreadsheet that splits
// columns on ";" — it will drop a comma-separated file into a single column.
const COMMA_DECIMAL_LOCALE = (1.1).toLocaleString().includes(",");

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ids of the picked fields — lives in the page so it survives closing the dialog. */
  selected: string[];
  onSelectedChange: (ids: string[]) => void;
  format: CallExportFormat;
  onFormatChange: (format: CallExportFormat) => void;
  onExport: () => void;
  onCancel: () => void;
  isExporting: boolean;
  progress: ExportProgress | null;
  callCount: number;
  hasFilters: boolean;
}

export function ExportDialog({
  open,
  onOpenChange,
  selected,
  onSelectedChange,
  format,
  onFormatChange,
  onExport,
  onCancel,
  isExporting,
  progress,
  callCount,
  hasFilters,
}: ExportDialogProps) {
  // A transcript per row is fine for a filtered slice and painful for a workspace.
  const warnAboutSize = hasHeavyExportField(selected) && callCount > 2000;
  const limit = exportRowLimit(selected);
  const willCap = callCount > limit;

  const progressLabel = !progress
    ? "Preparing…"
    : progress.stage === "calls"
      ? `Fetched ${progress.done.toLocaleString()} of ${progress.total.toLocaleString()} calls…`
      : `Loading details ${progress.done.toLocaleString()} of ${progress.total.toLocaleString()}…`;

  const toggle = (id: string, checked: boolean) => {
    // Registry order, so the file's columns don't depend on click order.
    onSelectedChange(
      CALL_EXPORT_FIELDS.filter((f) =>
        f.id === id ? checked : selected.includes(f.id),
      ).map((f) => f.id),
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !isExporting && onOpenChange(next)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Export calls</DialogTitle>
          <DialogDescription>
            {callCount.toLocaleString()} {callCount === 1 ? "call" : "calls"}
            {hasFilters ? " matching the current filters" : ""} — pick the columns to include.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-4">
          <span className="text-sm font-medium">Format</span>
          <RadioGroup
            value={format}
            onValueChange={(value) => onFormatChange(value as CallExportFormat)}
            disabled={isExporting}
            className="flex items-center gap-4"
          >
            {FORMATS.map((option) => (
              <div key={option.value} className="flex items-center gap-2">
                <RadioGroupItem value={option.value} id={`export-format-${option.value}`} />
                <Label
                  htmlFor={`export-format-${option.value}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <ScrollArea className="max-h-[45vh] pr-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
            {CALL_EXPORT_FIELDS.map((field) => (
              <div key={field.id} className="flex items-center gap-2">
                <Checkbox
                  id={`export-${field.id}`}
                  checked={selected.includes(field.id)}
                  onCheckedChange={(checked) => toggle(field.id, checked === true)}
                  disabled={isExporting}
                />
                <Label
                  htmlFor={`export-${field.id}`}
                  className="text-sm font-normal cursor-pointer truncate"
                  title={field.label}
                >
                  {field.label}
                  {field.needs && (
                    <span className="ml-1.5 text-xs text-muted-foreground">slower</span>
                  )}
                </Label>
              </div>
            ))}
          </div>
        </ScrollArea>

        {format === "csv" && COMMA_DECIMAL_LOCALE && (
          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <Info className="h-4 w-4 shrink-0 mt-px" />
            Your system separates decimals with a comma, so Excel here may open a .csv with
            every row in one column. Excel (.xlsx) opens correctly either way.
          </p>
        )}

        {willCap && (
          <p className="flex items-start gap-2 text-xs text-amber-600 dark:text-amber-500">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-px" />
            Only the first {limit.toLocaleString()} calls will be included — narrow the filters
            to export the rest.
          </p>
        )}

        {warnAboutSize && !willCap && (
          <p className="flex items-start gap-2 text-xs text-amber-600 dark:text-amber-500">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-px" />
            Transcripts for {callCount.toLocaleString()} calls will take a while and make a
            large file — narrowing the filters first is usually faster.
          </p>
        )}

        <DialogFooter className="sm:justify-between gap-2">
          {isExporting ? (
            <span className="text-xs text-muted-foreground self-center">{progressLabel}</span>
          ) : (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSelectedChange(CALL_EXPORT_FIELDS.map((f) => f.id))}
              >
                Select all
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSelectedChange(DEFAULT_CALL_EXPORT_FIELD_IDS)}
              >
                Reset
              </Button>
            </div>
          )}
          <div className="flex gap-2">
            {isExporting && (
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button onClick={onExport} disabled={isExporting || selected.length === 0}>
              {isExporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {isExporting ? "Exporting…" : `Export ${format === "csv" ? ".csv" : ".xlsx"}`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
